import { passwordRegex } from "app/helpers/passwordHelper";
import React, { Component } from "react";
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  type IntlShape,
} from "react-intl";
import * as profileAPI from "../../../../../api/profileApi";
import { TextInput } from "../../../../../components/FormInputs/FormInputs";
import type { PersonalDetailsType } from "../../../../../types/personalDetailsType";
import Dropdown from "./components/Dropdown";
import ErrorMessage from "./components/ErrorMessage";
import "./editPersonalDetails.scss";
type Props = {
  loadPersonalDetails: (...args: Array<any>) => any;
  personalDetails: PersonalDetailsType;
  isPersonalDetailsLoading: boolean;
  isAmsAccount: boolean;
  intl: IntlShape;
};
type State = {
  countries: any;
  email: string;
  emailError: string;
  emailErrorText: string;
  phone: string;
  addressLine1: string;
  addressLine1Error: string;
  addressLine1ErrorText: string;
  addressLine2: string;
  addressLine3: string;
  addressRegion: string;
  addressCity: string;
  addressCityError: string;
  addressCityErrorText: string;
  addressPostCode: string;
  addressPostCodeError: string;
  addressPostCodeErrorText: string;
  addressCountry: string;
  addressCountryId: string;
  userId: string;
  accountId: string;
  password: string;
  passwordError: string;
  passwordErrorText: string;
  phoneError: string;
  phoneErrorText: string;
  changePersonalDetailsSuccess: string;
  changePersonalDetailsFailed: string | any;
};

class EditPersonalDetails extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      email: props.account["email"],
      emailError: "",
      emailErrorText: "",
      phone: props.account["contactDetails"]["phoneNumber"],
      addressLine1: props.account["address"]["firstLine"],
      addressLine1Error: "",
      addressLine1ErrorText: "",
      addressLine2: props.account["address"]["secondLine"],
      addressLine3: props.account["address"]["thirdLine"],
      addressRegion: props.account["address"]["region"],
      addressCity: props.account["address"]["city"],
      addressPostCode: props.account["address"]["postalCode"] || "",
      addressPostCodeError: "",
      addressPostCodeErrorText: "",
      addressCountry: props.account["address"]["country"],
      addressCountryId: props.account["address"]["countryId"],
      userId: props.account["userId"],
      accountId: props.account["accountId"],
      password: "",
      passwordError: "",
      passwordErrorText: "",
      phoneError: "",
      phoneErrorText: "",
      changePersonalDetailsSuccess: "",
      changePersonalDetailsFailed: "",
      addressCityError: "",
      addressCityErrorText: "",
      countries: [],
    };
    // need to clean up how form deals with onSubmit
    this.onSubmit = this.onSubmit.bind(this);
  }

  // Validates the values entered into the fields using Regex
  validate = () => {
    const {
      email,
      phone,
      password,
      addressLine1,
      addressCity,
      addressPostCode,
      addressCountry,
    } = this.state;
    let isError = false;
    const errors = {
      emailError: "",
      emailErrorText: "",
      passwordError: "",
      passwordErrorText: "",
      phoneError: "",
      phoneErrorText: "",
      addressLine1Error: "",
      addressLine1ErrorText: "",
      addressCityError: "",
      addressCityErrorText: "",
      addressPostCodeError: "",
      addressPostCodeErrorText: "",
      addressCountryError: "",
      addressCountryErrorText: "",
    };

    // Regular expressions for validation
    // Validates a phone number consisting only of digits
    const phoneRegex = /^\d+$/;
    // Validates by ensuring it's not empty and contains at least one non-whitespace character
    const addressLine1Regex = /\S+/;
    const cityRegex = /\S+/;
    const postCodeRegex = /\S+/;
    const countryRegex = /\S+/;
    // Validates an email address based on common structure and standards
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    // checking to see if input passes regex, if it does not we assign a string to a state
    if (!emailRegex.test(String(email).toLowerCase())) {
      isError = true;
      errors.emailError = "error";
      errors.emailErrorText = "Invalid email address";
    }

    // phone no validation check
    if (phone.length === 0 || !phoneRegex.test(phone)) {
      isError = true;
      errors.phoneError = "error";
      errors.phoneErrorText =
        "Invalid phone number (must contain no white spaces and only digits).";
    } else if (phone.length < 8) {
      isError = true;
      errors.phoneError = "error";
      errors.phoneErrorText =
        "Invalid phone number (must be at least 8 digits).";
    }

    // password validation check
    if (!passwordRegex.test(password)) {
      isError = true;
      errors.passwordError = "error";
      errors.passwordErrorText = this.props.intl.formatMessage({
        id: "password-validation-message",
        defaultMessage:
          "Password must be 8-50 characters long and contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character",
      });
    }

    // address line 1 validation check
    if (!addressLine1Regex.test(addressLine1)) {
      isError = true;
      errors.addressLine1Error = "error";
      errors.addressLine1ErrorText = "Address line 1 cannot be blank";
    }

    // city validation check
    if (!cityRegex.test(addressCity)) {
      isError = true;
      errors.addressCityError = "error";
      errors.addressCityErrorText = "City cannot be blank";
    }

    // code validation check
    if (!postCodeRegex.test(addressPostCode)) {
      isError = true;
      errors.addressPostCodeError = "error";
      errors.addressPostCodeErrorText = "Code cannot be blank";
    }

    // country validation check
    if (!countryRegex.test(addressCountry)) {
      isError = true;
      errors.addressCountryError = "error";
      errors.addressCountryErrorText = "Country cannot be blank";
    }

    this.setState({ ...this.state, ...errors });
    return isError;
  };

  handleCountryChange(e) {
    e.preventDefault();
    this.setState({
      addressCountryId: e.target.value,
      addressCountry: e.target.options[e.target.selectedIndex].text,
    });
  }

  onSubmit = e => {
    e.preventDefault();
    const err = this.validate();

    if (!err) {
      // clear form
      this.setState({
        emailError: "",
        emailErrorText: "",
        passwordError: "",
        passwordErrorText: "",
        phoneError: "",
        phoneErrorText: "",
        addressLine1Error: "",
        addressLine1ErrorText: "",
      });
      const {
        email,
        phone,
        addressLine1,
        addressLine2,
        addressLine3,
        addressRegion,
        addressCity,
        addressPostCode,
        addressCountry,
        addressCountryId,
        userId,
        accountId,
        password,
      } = this.state;
      const data = {
        email: email,
        address: {
          firstLine: addressLine1,
          secondLine: addressLine2,
          thirdLine: addressLine3,
          region: addressRegion,
          city: addressCity,
          postalCode: addressPostCode,
          country: addressCountry,
          countryId: addressCountryId,
        },
        contactDetails: {
          phoneNumber: phone,
        },
        password: password,
        userId: userId,
        accountId: accountId,
      };
      profileAPI
        .updateAccountDetails(data)
        .then(resp => {
          // give user feedback on success
          let successMessage = (
            <span className="success">success personal details updated</span>
          );
          this.setState({
            changePersonalDetailsFailed: successMessage,
          });
        })
        .catch(error => {
          let failedMessage = (
            <span className="failed">personal details update failed</span>
          );
          this.setState({
            changePersonalDetailsFailed: failedMessage,
          });
        });
    }
  };
  handleInput = (id, value) => {
    this.setState({
      [id]: value,
    });
  };

  render() {
    const I18n = defineMessages({
      username: {
        id: "settings-username",
      },
      title: {
        id: "settings-title",
      },
      email: {
        id: "settings-email",
      },
      update: {
        id: "settings-update",
      },
      phone: {
        id: "settings-phone",
      },
      settingsAddress1: {
        id: "settings-address1",
      },
      settingsAddress2: {
        id: "settings-address2",
      },
      settingsAddress3: {
        id: "settings-address3",
      },
      settingsRegion: {
        id: "settings-region",
      },
      settingsCity: {
        id: "settings-city",
      },
      settingsCode: {
        id: "settings-code",
      },
      settingsCountry: {
        id: "settings-country",
      },
      settingsPassword: {
        id: "settings-typepassword",
      },
    });
    return (
      <React.Fragment>
        <form
          className="personal-details__component--form"
          onSubmit={this.onSubmit}
        >
          <TextInput
            type="email"
            id="email"
            onChange={this.handleInput}
            label={I18n.email}
            value={this.state.email}
            className={`${this.state.emailError} ${
              this.props.isAmsAccount
                ? "transparent"
                : "personal-details--input"
            }`}
            disabled={this.props.isAmsAccount}
          />
          {/* ErrorMessage component displays a message to the user when regex validation fails */}
          <ErrorMessage errorText={this.state.emailErrorText} />
          <TextInput
            type="tel"
            id="phone"
            onChange={this.handleInput}
            label={I18n.phone}
            value={this.state.phone}
            className={`${this.state.phoneError} ${
              this.props.isAmsAccount
                ? "transparent"
                : "personal-details--input"
            }`}
            disabled={this.props.isAmsAccount}
          />
          <ErrorMessage errorText={this.state.phoneErrorText} />
          <TextInput
            type="text"
            id="addressLine1"
            onChange={this.handleInput}
            label={I18n.settingsAddress1}
            value={this.state.addressLine1}
            className={`${this.state.addressLine1Error} ${
              this.props.isAmsAccount
                ? "transparent"
                : "personal-details--input"
            }`}
            disabled={this.props.isAmsAccount}
          />
          <ErrorMessage errorText={this.state.addressLine1ErrorText} />
          <TextInput
            type="text"
            id="addressLine2"
            onChange={this.handleInput}
            label={I18n.settingsAddress2}
            value={this.state.addressLine2}
            className={`${
              this.props.isAmsAccount
                ? "transparent"
                : "personal-details--input"
            }`}
            disabled={this.props.isAmsAccount}
          />
          <TextInput
            type="text"
            id="addressLine3"
            onChange={this.handleInput}
            label={I18n.settingsAddress3}
            value={this.state.addressLine3}
            className={`${
              this.props.isAmsAccount
                ? "transparent"
                : "personal-details--input"
            }`}
            disabled={this.props.isAmsAccount}
          />
          <TextInput
            type="text"
            id="addressRegion"
            onChange={this.handleInput}
            label={I18n.settingsRegion}
            value={this.state.addressRegion}
            className={`${
              this.props.isAmsAccount
                ? "transparent"
                : "personal-details--input"
            } `}
            disabled={this.props.isAmsAccount}
          />
          <TextInput
            type="text"
            id="addressCity"
            onChange={this.handleInput}
            label={I18n.settingsCity}
            value={this.state.addressCity}
            className={` ${this.state.addressCityError} ${
              this.props.isAmsAccount
                ? "transparent"
                : "personal-details--input"
            }`}
            disabled={this.props.isAmsAccount}
          />
          <ErrorMessage errorText={this.state.addressCityErrorText} />
          <TextInput
            type="text"
            id="addressPostCode"
            onChange={this.handleInput}
            label={I18n.settingsCode}
            value={this.state.addressPostCode}
            className={`${this.state.addressPostCodeError} ${
              this.props.isAmsAccount
                ? "transparent"
                : "personal-details--input"
            }`}
            disabled={this.props.isAmsAccount}
          />
          <ErrorMessage errorText={this.state.addressPostCodeErrorText} />

          {/* Added a component to display the select tag as a dropdown component */}
          <div>
            <Dropdown
              value={this.state.addressCountry}
              options={[
                {
                  value: "",
                  label: this.state.addressCountry || "Select Country",
                },
                ...this.props.countries.map(country => ({
                  value: country.id,
                  label: country.name,
                })),
              ]}
              onChange={this.handleCountryChange.bind(this)}
              disabled={this.props.isAmsAccount}
              label={"Country"}
            />
          </div>
          {this.state.addressPostCodeErrorText}

          {this.props.isAmsAccount ? (
            <div />
          ) : (
            <div>
              <TextInput
                autoComplete={"current password"}
                type="password"
                id="password"
                onChange={this.handleInput}
                label={I18n.settingsPassword}
                className={`${this.state.passwordError} ${
                  this.props.isAmsAccount
                    ? "transparent"
                    : "personal-details--input"
                }`}
                disabled={this.props.isAmsAccount}
              />
              <ErrorMessage errorText={this.state.passwordErrorText} />
              <div className="personal__details__buttons">
                <button
                  type="submit"
                  className="personal__details__submit-button"
                  disabled={this.props.isAmsAccount}
                  id="personal_details_submit_button"
                >
                  <FormattedMessage tagName="span" id="settings-update" />
                </button>
              </div>
            </div>
          )}
          <span className="msg">
            {this.state.changePersonalDetailsSuccess}
            {this.state.changePersonalDetailsFailed}
          </span>
        </form>
      </React.Fragment>
    );
  }
}

export default injectIntl(EditPersonalDetails);
